import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, ASSETS } from "../i18n";

const links = [
  { id: "services", key: "services" },
  { id: "pricing", key: "auto" },
  { id: "gallery", key: "gallery" },
  { id: "contact", key: "contact" },
];

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-20 md:h-24" : "h-24 md:h-28"}`}>
        <button onClick={() => go("top")} data-testid="logo-home" className="flex items-center gap-3 md:gap-4 no-tap group">
          <img
            src={ASSETS.logo}
            alt="45 Detailling Geneva"
            className={`w-auto transition-all duration-500 drop-shadow-[0_4px_16px_rgba(212,175,55,0.25)] group-hover:drop-shadow-[0_6px_22px_rgba(212,175,55,0.45)] ${scrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`}
          />
          <div className="leading-none text-left">
            <p className="font-display gold-text font-semibold text-2xl sm:text-3xl md:text-[2rem] tracking-tight">
              45 Detailling
            </p>
            <p className="overline text-[0.55rem] sm:text-[0.65rem] tracking-[0.42em] mt-1.5">
              Geneva · Genève
            </p>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-neutral-300 hover:text-[var(--c-gold)] transition-colors"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="font-mono text-xs px-3 py-2 rounded-full gold-border text-[var(--c-gold)] hover:bg-[var(--c-gold)] hover:text-black transition-colors"
          >
            {lang === "fr" ? "FR / EN" : "EN / FR"}
          </button>
          <button
            data-testid="cta-header-book"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full bg-[var(--c-gold)] text-black hover:bg-[var(--c-gold-soft)] transition-colors"
          >
            {t.nav.book}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-neutral-200 p-1"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-black/95 border-t border-white/5 px-6 py-5 flex flex-col gap-4"
        >
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left text-base text-neutral-200 hover:text-[var(--c-gold)]"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </motion.div>
      )}
    </header>
  );
};
